import { Record, Map, RecordOf } from 'immutable';

import { GENERIC_ERROR } from 'constants/errors.constants';
import { Actions } from './actions';
import { Charge, Transaction, Refund } from 'services/resources';

const TransactionRecordFactory = Record<Transaction>({
  id: null,
  charges: [],
  refunds: [],
  amount: 0.0,
  currency: null,
  amountRefunded: 0.0,
  disputed: false,
  paymentMethod: {
    date: null,
    status: null,
    method: null,
    reference: null,
  },
  agentName: null,
});

const ChargeRecordFactory = Record<Charge>({
  agreementId: null,
  autofill: false,
  baseCharge: 0,
  category: null,
  code: null,
  description: null,
  id: null,
  name: null,
  note: null,
  overridable: false,
  price: 0,
  quantity: 0,
  quantityUnit: null,
  taxes: [],
  dealership: null,
});

export const RefundRecordFactory = Record<Refund>({
  id: null,
  agent: null,
  amount: 0,
  processedDate: null,
  note: null,
  reason: null,
  transaction: null,
});

const TransactionsStateRecordFactory = Record({
  errors: Record({})(),
  loading: false,
  transaction: null,
  transactions: {
    count: 0,
    offset: 0,
    next: '',
    previous: '',
    results: [],
  },
  transaction_storage: Map<string, RecordOf<Transaction>>({}),
  charge_storage: Map<string, RecordOf<Charge>>({}),
  refund_storage: Map<string, RecordOf<Refund>>({}),
});

export const initialState = TransactionsStateRecordFactory();

type TransactionAction = {
  type: Actions;
  payload: any;
};

const transactionsServiceReducer = (state = initialState, action: TransactionAction) => {
  switch (action.type) {
    case Actions.GET_TRANSACTION_RETRIEVE_SUCCESS:
      return state.setIn(
        ['transaction_storage', action.payload.transaction.id],
        TransactionRecordFactory(action.payload.transaction)
      );
    case Actions.GET_TRANSACTION_LIST_SUCCESS: {
      const overwritingTransactions = {};

      action.payload.transactions.results.forEach((transaction) => {
        overwritingTransactions[transaction.id] = TransactionRecordFactory(transaction);
      });

      return state.mergeIn(['transaction_storage'], overwritingTransactions);
    }
    case Actions.GET_CHARGE_RETRIEVE_SUCCESS:
      return state.setIn(['charge_storage', action.payload.charge.id], ChargeRecordFactory(action.payload.charge));
    case Actions.GET_CHARGE_LIST_SUCCESS: {
      const overwritingCharges = {};

      action.payload.charges.results.forEach((charge) => {
        overwritingCharges[charge.id] = ChargeRecordFactory(charge);
      });

      return state.mergeIn(['charge_storage'], overwritingCharges);
    }
    case Actions.GET_REFUND_RETRIEVE_SUCCESS:
      return state.setIn(['refund_storage', action.payload.refund.id], RefundRecordFactory(action.payload.refund));
    case Actions.GET_REFUND_LIST_SUCCESS: {
      const overwritingRefunds = {};

      action.payload.refunds.results.forEach((refund) => {
        overwritingRefunds[refund.id] = RefundRecordFactory(refund);
      });

      return state.mergeIn(['refund_storage'], overwritingRefunds);
    }
    case Actions.ADD_REFUND_SUCCESS:
      return state
        .mergeIn(['refund_storage'], { [action.payload.refund.id]: RefundRecordFactory(action.payload.refund) })
        .updateIn(
          ['transaction_storage', action.payload.refund.transaction.toString()],
          (transaction: RecordOf<Transaction>) => {
            return transaction.merge({
              refunds: [...transaction.get('refunds'), action.payload.refund.id],
              amountRefunded: transaction.get('amountRefunded') + action.payload.refund.amount,
            });
          }
        );
    case Actions.ADD_REFUND_FAILURE:
    case Actions.GET_CHARGE_LIST_FAILURE:
    case Actions.GET_CHARGE_RETRIEVE_FAILURE:
    case Actions.GET_REFUND_LIST_FAILURE:
    case Actions.GET_REFUND_RETRIEVE_FAILURE:
    case Actions.GET_TRANSACTION_RETRIEVE_FAILURE:
    case Actions.GET_TRANSACTION_LIST_FAILURE:
      return state.merge({
        loading: false,
        errors: action.payload.errors || GENERIC_ERROR,
      });
    default:
      return state;
  }
};

export default transactionsServiceReducer;
